import config from 'config';
import { SortId } from 'consts/sort';
import Message from 'types/Message';
import SearchItem from 'types/SearchItem';
import { ProductTypeResult } from 'graphql/queries/GetProductTypes.query';
import SearchKeyPress from '@components/keypress/SearchKeyPress';
import pick from 'utils/pick';
import omit from 'utils/omit';
import useRouteState from 'hooks/useRouteState';

import SearchView from './SearchController.view';
import { UseSearchStateReturn } from './useSearchState';

const { routes } = config;

export type FallbackProps = {
  header?: Message;
  data?: SearchItem[];
  totalItems?: number;
  mainProductTypeId?: Nullable<number>;
  productTypes?: ProductTypeResult[];
};

export type Props = FallbackProps & UseSearchStateReturn;

const SearchContainer = ({
  header,
  data,
  totalItems = 0,
  mainProductTypeId = null,
  productTypes,
  // from hook
  page,
  limit,
  currentSort,
  isAvailable,
  combinedQuery,
  filtersQuery,
  mappedFilters,
  mappedAdvancedFilters,
  filterTags,
  numberOfCheckedFilters,
  getHref,
  onChangeQuery,
  getFilterChangeHref,
  getFilterClearHref,
  onFilterChange,
  onSubmit,
}: Props): JSX.Element | null => {
  const { isLoading } = useRouteState({ url: routes.search.href });

  return (
    <>
      <SearchKeyPress query={combinedQuery} />
      <SearchView
        header={header}
        data={data}
        filters={mappedFilters}
        filterTags={filterTags}
        advancedFilters={mappedAdvancedFilters}
        productTypes={!mainProductTypeId ? productTypes : []}
        numberOfCheckedFilters={numberOfCheckedFilters}
        initialFiltersValues={filtersQuery}
        loading={isLoading}
        currentPage={page}
        totalPage={Math.max(Math.ceil(totalItems / limit), 1)}
        totalItems={totalItems}
        paginationSize={limit}
        isAvailable={isAvailable}
        selectedSort={currentSort}
        onFilterChange={onFilterChange}
        onFilterClear={() => {
          onChangeQuery({
            ...pick(filtersQuery, ['name', 'limit', 'product_type', 'sort_id', 'is_available']),
            page: undefined,
          });
        }}
        onSortChange={(newSort) => {
          onChangeQuery({
            ...combinedQuery,
            sort_id: newSort === SortId.Popularity ? undefined : newSort,
          });
        }}
        onSortClear={() => {
          onChangeQuery({
            ...omit(combinedQuery, ['sort_id']),
            page: undefined,
          });
        }}
        onSubmit={onSubmit}
        getFilterChangeHref={getFilterChangeHref}
        getFilterClearHref={getFilterClearHref}
        availableChangeHref={getHref({
          ...combinedQuery,
          is_available: isAvailable ? undefined : '1',
          page: undefined,
        })}
        filterClearHref={getHref(
          {
            ...pick(filtersQuery, ['name', 'limit', 'product_type', 'sort_id', 'is_available']),
            page: undefined,
          },
          true
        )}
        filterTagsClearHref={getHref(
          {
            ...pick(filtersQuery, ['limit', 'product_type', 'sort_id', 'is_available']),
            page: undefined,
          },
          true
        )}
        getHref={getHref}
        getLimitChangeHref={(newSize) =>
          getHref({
            ...combinedQuery,
            limit: newSize === 20 ? undefined : newSize,
            page: undefined,
          })
        }
        getSortChangeHref={(newSort) =>
          getHref({
            ...combinedQuery,
            sort_id: newSort === SortId.Popularity ? undefined : newSort,
          })
        }
        getPageChangeHref={(newPage) =>
          getHref({
            ...combinedQuery,
            page: newPage,
          })
        }
      />
    </>
  );
};

export default SearchContainer;
